import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { Button, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { getUserTokenFromLocalStorage, removeUserTokenFromLocalStorage } from '@utils/localStorage';
import { isUserAdmin, isViewOnlyPermission, userHasPermission } from '@utils/access';
import { Breakpoints } from '@utils/constants';
import { UserPermissions, type User } from '@models/user';

import { useNavigationGuard } from '@hooks/useNavigationGuard';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useMeStore } from '@features/users/store';
import { logoutMe } from '@features/users/api';

import { Logo } from '@assets/DashboardLayout/Logo';
import AccessDenied from '@components/ui/AccessDenied';

import s from './DashboardLayout.module.css';

const UserProfile = () => {
  const isMobile = useMediaQuery({ query: `(max-width: ${Breakpoints.md})` });
  const me = useMeStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = async () => {
    await logoutMe();
    removeUserTokenFromLocalStorage();

    window.location.href = '/';
    return;
  };

  const handleProfileClick = () => {
    navigate('/dashboard/profile');
  };

  return (
    <>
      <div className={`${isMobile ? s.userProfileContainerMobile : s.userProfileContainer}`}>
        <Button
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          className={s.userProfileButton}>
          <img src={me?.photoUrl} alt="User Avatar" className={s.userAvatar} />
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}>
          <MenuItem onClick={handleProfileClick} className={s.menuItem}>
            Profile
          </MenuItem>
          <MenuItem onClick={handleLogOut} className={s.menuItem}>
            Logout
          </MenuItem>
        </Menu>
      </div>
    </>
  );
};

type NavigationProps = {
  activeStep: string;
  me: User;
  onNavigate: (to: string) => void;
};

const isNavLinkActive = (activeStep: string, to: string) =>
  activeStep === to || activeStep.startsWith(`${to}/`);

const isGroupActive = (activeStep: string, group: NavGroup) =>
  group.items.some((item) => item.type === 'route' && isNavLinkActive(activeStep, item.to));

const MobileNavigation = ({ activeStep, me, onNavigate }: NavigationProps) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { links, groups } = getNavigation(me);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const renderItem = (item: NavItem) =>
    item.type === 'route' ? (
      <button
        type="button"
        key={item.to}
        onClick={() => onNavigate(item.to)}
        className={`${s.navLinkMobile} ${isNavLinkActive(activeStep, item.to) ? s.navLinkMobileActive : ''}`}>
        <span>{item.label}</span>
      </button>
    ) : (
      <ExternalTokenLink
        key={item.href}
        className={s.navLinkMobile}
        href={item.href}
        label={item.label}
      />
    );

  return (
    <div className={s.mobileNavigationContainer}>
      <button type="button" className={s.mobileNavButton} onClick={handleMenuClick}>
        <MenuIcon />
      </button>
      {isMenuOpen && (
        <div className={s.mobileMenu}>
          <button type="button" className={s.mobileNavCloseButton} onClick={handleMenuClick}>
            <MenuOpenIcon />
          </button>
          <UserProfile />
          <div className={s.mobileInnerWrapper}>
            {links.map(renderItem)}
            {groups.map((group) => (
              <div key={group.label} className={s.mobileNavGroup}>
                <span className={s.mobileNavGroupLabel}>{group.label}</span>
                {group.items.map(renderItem)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DesktopNavigation = ({ activeStep, me, onNavigate }: NavigationProps) => {
  const { links, groups } = getNavigation(me);

  return (
    <>
      <div className={s.navigationLinks}>
        {links.map((link) => (
          <button
            type="button"
            key={link.to}
            className={`${s.navLink} ${isNavLinkActive(activeStep, link.to) ? s.navLinkActive : ''}`}
            onClick={() => onNavigate(link.to)}>
            <span>{link.label}</span>
          </button>
        ))}
        {groups.map((group) => (
          <NavGroupMenu
            key={group.label}
            group={group}
            isActive={isGroupActive(activeStep, group)}
            activeStep={activeStep}
            onNavigate={onNavigate}
          />
        ))}
      </div>
      <div className={s.userProfile}>
        <UserProfile />
      </div>
    </>
  );
};

type NavGroupMenuProps = {
  group: NavGroup;
  isActive: boolean;
  activeStep: string;
  onNavigate: (to: string) => void;
};

// A header dropdown: the trigger looks like a nav link and is underlined while one of its pages is open.
const NavGroupMenu = ({ group, isActive, activeStep, onNavigate }: NavGroupMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuId = `nav-group-${group.label.toLowerCase()}`;
  const buttonId = `${menuId}-button`;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (to: string) => {
    handleClose();
    onNavigate(to);
  };

  return (
    <>
      <button
        type="button"
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        className={`${s.navLink} ${isActive ? s.navLinkActive : ''}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}>
        <span>{group.label}</span>
        <ArrowDropDownIcon className={s.navGroupArrow} />
      </button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': buttonId,
          },
        }}>
        {group.items.map((item) =>
          item.type === 'route' ? (
            <MenuItem
              key={item.to}
              selected={isNavLinkActive(activeStep, item.to)}
              onClick={() => handleNavigate(item.to)}>
              {item.label}
            </MenuItem>
          ) : (
            <MenuItem
              key={item.href}
              component="a"
              href={item.href}
              target="_blank"
              rel="noreferrer"
              onClick={handleClose}>
              {item.label}
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  );
};

type ExternalLinkProps = {
  className: string;
  href: string;
  label: string;
};

const ExternalTokenLink = ({ className, href, label }: ExternalLinkProps) => (
  <a href={href} target="_blank" rel="noreferrer" className={`${className} ${s.externalNavLink}`}>
    <span>{label}</span>
  </a>
);

// Bull Board runs in payments-api's worker process. Staging and production proxy it under the API host,
// so VITE_BULL_BOARD_URL only needs setting where the worker answers on a host of its own — locally it
// listens on WORKERS_PORT, not the API port.
const BULL_BOARD_BASE_URL = import.meta.env.VITE_BULL_BOARD_URL || import.meta.env.VITE_API_URL;

// Swagger and Bull Board are served by payments-api itself and can't read the admin's localStorage, so
// the token rides along in the query string — their middleware swaps it for a cookie and strips it.
const externalTokenHref = (baseUrl: string, path: string, token: string) =>
  `${baseUrl}${path}?accessToken=${encodeURIComponent(token)}`;

const PAYMENTS_PATH = '/dashboard/payments';
const CUSTOMERS_PATH = '/dashboard/customers';
const INCIDENTS_PATH = '/dashboard/incidents';
// Read-only pages a view-only account may still open. Their write actions (capture, cancel debts,
// new incident, ...) are hidden behind users.update by the buttons themselves.
const VIEW_ONLY_PATHS = [PAYMENTS_PATH, CUSTOMERS_PATH, INCIDENTS_PATH];

type RouteNavItem = { type: 'route'; to: string; label: string };
type ExternalNavItem = { type: 'external'; href: string; label: string };
type NavItem = RouteNavItem | ExternalNavItem;
type NavGroup = { label: string; items: NavItem[] };

type RouteLink = { to: string; label: string; permissionNeeded: string };

const TopLevelLinks: RouteLink[] = [
  { to: '/dashboard/charger-flows', label: 'Charger Flows', permissionNeeded: '' },
  { to: PAYMENTS_PATH, label: 'Payments', permissionNeeded: '' },
  { to: CUSTOMERS_PATH, label: 'Customers', permissionNeeded: '' },
];

const ManageLinks: RouteLink[] = [
  { to: INCIDENTS_PATH, label: 'Incidents', permissionNeeded: '' },
  { to: '/dashboard/settings', label: 'Settings', permissionNeeded: '' },
  { to: '/dashboard/intermediary-data', label: 'Intermediary Analytics', permissionNeeded: '' },
  { to: '/dashboard/users', label: 'Users', permissionNeeded: UserPermissions.VIEW_USERS },
];

const visibleRouteItems = (me: User, links: RouteLink[]): RouteNavItem[] =>
  links
    .filter((link) => !isViewOnlyPermission(me) || VIEW_ONLY_PATHS.includes(link.to))
    .filter((link) => !link.permissionNeeded || userHasPermission(me, link.permissionNeeded))
    .map(({ to, label }) => ({ type: 'route', to, label }));

const toolItems = (me: User): ExternalNavItem[] => {
  const token = getUserTokenFromLocalStorage();
  if (!token) return [];

  const items: ExternalNavItem[] = [];
  if (!isViewOnlyPermission(me)) {
    items.push({
      type: 'external',
      label: 'API Docs',
      href: externalTokenHref(import.meta.env.VITE_API_URL, '/docs', token),
    });
  }
  if (isUserAdmin(me)) {
    items.push({
      type: 'external',
      label: 'BullMQ',
      href: externalTokenHref(BULL_BOARD_BASE_URL, '/workers/bullui', token),
    });
  }
  return items;
};

// Top-level links stay in the header bar; the rest fold into dropdown groups. A group with nothing
// visible for this account (view-only, non-admin, no token) is dropped entirely.
function getNavigation(me: User): { links: RouteNavItem[]; groups: NavGroup[] } {
  const groups: NavGroup[] = [
    { label: 'Manage', items: visibleRouteItems(me, ManageLinks) },
    { label: 'Tools', items: toolItems(me) },
  ];

  return {
    links: visibleRouteItems(me, TopLevelLinks),
    groups: groups.filter((group) => group.items.length > 0),
  };
}

function DashboardLayout() {
  const isMobile = useMediaQuery({ query: `(max-width: ${Breakpoints.md})` });
  const location = useLocation();
  const activeStep = location.pathname;
  const isFlowsPage =
    location.pathname.includes('/dashboard/charger-flows') && location.pathname.includes('edit');
  const meStore = useMeStore();
  const guardedNavigate = useNavigationGuard();

  if (!meStore) return null;

  // View-only accounts get the read-only pages (payments, customers, incidents) and nothing else.
  const accessDenied =
    isViewOnlyPermission(meStore) &&
    !VIEW_ONLY_PATHS.some((path) => location.pathname.startsWith(path));

  // Prevent navigation if there are unsaved changes contolled thorugh Zustand store
  const handleNavigate = (to: string) => {
    guardedNavigate(to);
  };

  return (
    <>
      <header className={s.headerWrapper}>
        <div className={s.headerContainer}>
          <button type="button" className={s.logoLink} onClick={() => handleNavigate('/dashboard')}>
            <Logo />
          </button>

          {isMobile ? (
            <MobileNavigation activeStep={activeStep} me={meStore} onNavigate={handleNavigate} />
          ) : (
            <DesktopNavigation activeStep={activeStep} me={meStore} onNavigate={handleNavigate} />
          )}
        </div>
      </header>

      <div className={!isFlowsPage ? s.contentWrapper : ''}>
        {accessDenied ? (
          <AccessDenied actionTo={CUSTOMERS_PATH} actionLabel="Go to Customers" />
        ) : (
          <Outlet />
        )}
      </div>
    </>
  );
}

export default DashboardLayout;
